import { ElementIdSchema, MethodIdSchema } from '@dpem/ontology';
import * as z from 'zod';

import {
  AutomationLevelSchema,
  CATALOG_KINDS,
  ELEMENT_KINDS,
  MethodStateSchema,
} from '../common/enums.js';
import { JsonLdDocumentSchema, UntrustedJsonLdSchema } from '../common/jsonld.js';
import { PageQuerySchema, paginated } from '../common/pagination.js';
import {
  DescriptionSchema,
  IdentifierSchema,
  IsoDateTimeSchema,
  LabelSchema,
  RevisionSchema,
  ShortTextSchema,
  TextFilterSchema,
  sortBy,
} from '../common/primitives.js';
import { MethodVersionSummarySchema } from './methods.js';
import { DiagnosticSchema, ValidationReportSchema } from './validation.js';

/**
 * publishing context — immutable published methods, JSON-LD export/import and
 * the generic two-version compare.
 *
 * Compare has no agentic special case (architecture §6, closed decision): it
 * joins any two versions on canonical element id. The agentic richness shows up
 * only as ontology property changes on the elements it finds.
 */

export const PUBLISHED_SORT_FIELDS = ['name', 'publishedAt', 'methodId'] as const;

/**
 * Which serialisation an export asks for.
 *
 * `graph` is the **default**, and that is the whole point of the pair. It is the normalised
 * node/edge shape third-party ontology processors require: `id`/`type` rather than
 * `@id`/`@type`, a four-value type vocabulary with the real class in `attributes.dpemType`,
 * every relation folded into one `relatesTo` array and every literal a string. Processors
 * refuse the canonical dialect outright — "Unsupported JSON-LD graph shape" — so a download
 * that did not say which dialect it wanted used to be the one that would not load.
 *
 * `canonical` is the platform's own dialect — `@id`/`@type` with `dpem:` classes and one
 * predicate per relation. It is the form the checksum and the byte-stable round-trip are
 * defined over, so it stays available and stays what publishing hashes.
 *
 * Import reads both: the fold keeps every predicate and the ontology class, so the compliant
 * export is not a dead end (`@dpem/ontology`'s `interop-parse`).
 */
export const ExportFormatSchema = z.enum(['canonical', 'graph']);
export type ExportFormat = z.infer<typeof ExportFormatSchema>;

export const ExportMethodRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  /** JSON indentation of the returned document text; 2 matches the ontology default. */
  indent: z.coerce.number().int().min(0).max(8).optional(),
  /** Absent means `graph`: a caller that does not choose a dialect gets the compliant one. */
  format: ExportFormatSchema.optional(),
});
export type ExportMethodRequest = z.infer<typeof ExportMethodRequestSchema>;
export const ExportMethodResponseSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  methodId: MethodIdSchema,
  state: MethodStateSchema,
  revision: RevisionSchema,
  /** Echoed so a caller that saved the file knows which dialect it holds. */
  format: ExportFormatSchema,
  document: JsonLdDocumentSchema,
  /** Byte-exact serialisation the checksum was taken over; export → import round-trips it. */
  documentText: z.string().min(2),
  checksum: z.string().regex(/^[0-9a-f]{64}$/),
  exportedAt: IsoDateTimeSchema,
  fileName: ShortTextSchema,
});
export type ExportMethodResponse = z.infer<typeof ExportMethodResponseSchema>;

export const ImportMethodRequestSchema = z.strictObject({
  /** Opaque here on purpose: the ontology parser owns judgement, we report it. */
  document: UntrustedJsonLdSchema,
  /** Validate and report only; nothing is written. */
  dryRun: z.boolean().optional(),
  /** Override the imported method's display name without touching its `@id`. */
  name: LabelSchema.optional(),
  /** Import as a variant of an existing version instead of a standalone method. */
  variantOfId: IdentifierSchema.optional(),
});
export type ImportMethodRequest = z.infer<typeof ImportMethodRequestSchema>;
export const ImportMethodResponseSchema = z.strictObject({
  /** Null for a dry run, or when parsing failed. */
  methodVersionId: IdentifierSchema.nullable(),
  dryRun: z.boolean(),
  accepted: z.boolean(),
  nodeCount: z.int().min(0),
  /** Structural parse issues from the ontology (`ParseIssue`). */
  parseIssues: z.array(z.strictObject({ path: z.string(), message: z.string() })),
  /** Semantic rule diagnostics; present only once the document parsed. */
  report: ValidationReportSchema.nullable(),
});
export type ImportMethodResponse = z.infer<typeof ImportMethodResponseSchema>;

export const PublishedMethodSchema = z.strictObject({
  ...MethodVersionSummarySchema.shape,
  publishedAt: IsoDateTimeSchema,
  publishedBy: ShortTextSchema,
  checksum: z.string().regex(/^[0-9a-f]{64}$/),
  /** Set when the version was published out of a workshop session. */
  workshopSessionId: IdentifierSchema.nullable(),
});
export type PublishedMethod = z.infer<typeof PublishedMethodSchema>;

export const ListPublishedRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  /**
   * `q` only, not the whole of `TextFilterSchema`: a published snapshot carries no tags
   * of its own, and the ones on the list rows come from `method_versions`, which belongs
   * to another context. Declaring `tags` here would take a filter the query cannot honour
   * and hand the caller a plausible page with half their request silently dropped.
   * Filtering the published catalogue by tag means denormalising them onto the snapshot at
   * publish time — where they are immutable, like everything else about it — and that is a
   * schema change, not a query parameter.
   */
  q: TextFilterSchema.shape.q,
  ...sortBy(PUBLISHED_SORT_FIELDS).shape,
  methodId: MethodIdSchema.optional(),
  variantOfId: IdentifierSchema.optional(),
});
export type ListPublishedRequest = z.infer<typeof ListPublishedRequestSchema>;
export const ListPublishedResponseSchema = paginated(PublishedMethodSchema);
export type ListPublishedResponse = z.infer<typeof ListPublishedResponseSchema>;

/* ── Compare ───────────────────────────────────────────────────────────── */

export const CHANGE_KINDS = ['added', 'removed', 'changed', 'unchanged'] as const;
export const ChangeKindSchema = z.enum(CHANGE_KINDS);
export type ChangeKind = z.infer<typeof ChangeKindSchema>;

export const PropertyChangeSchema = z.strictObject({
  property: ShortTextSchema,
  left: z.unknown().optional(),
  right: z.unknown().optional(),
});
export type PropertyChange = z.infer<typeof PropertyChangeSchema>;

/** Human role on the left, the agent (or agentic role) that absorbs it on the right. */
export const RoleMappingSchema = z.strictObject({
  leftRoleId: ElementIdSchema.nullable(),
  leftRoleName: LabelSchema.nullable(),
  rightRoleId: ElementIdSchema.nullable(),
  rightRoleName: LabelSchema.nullable(),
  agentId: ElementIdSchema.nullable(),
  automation: AutomationLevelSchema.nullable(),
});
export type RoleMapping = z.infer<typeof RoleMappingSchema>;

export const EffortShiftSchema = z.strictObject({
  /**
   * Ontology `effortShift` verbatim, e.g. "-40% analyst effort" — but "verbatim" is the
   * operative word, and DPSE's own stages write a paragraph there, so the cap matches the
   * authoring field it quotes rather than being narrower than its own source.
   */
  label: DescriptionSchema.nullable(),
  /** Parsed percentage when the label carried one; negative means less human effort. */
  percent: z.number().min(-100).max(1_000).nullable(),
});
export type EffortShift = z.infer<typeof EffortShiftSchema>;

export const GateModelChangeSchema = z.strictObject({
  gateId: ElementIdSchema,
  gateName: LabelSchema,
  leftExitCriteria: DescriptionSchema.nullable(),
  rightExitCriteria: DescriptionSchema.nullable(),
  leftAutomatedCheck: DescriptionSchema.nullable(),
  rightAutomatedCheck: DescriptionSchema.nullable(),
  leftHumanApproval: DescriptionSchema.nullable(),
  rightHumanApproval: DescriptionSchema.nullable(),
  /** True when the gate moved from human sign-off to automated check plus approval. */
  modelChanged: z.boolean(),
});
export type GateModelChange = z.infer<typeof GateModelChangeSchema>;

export const CycleTimeDeltaSchema = z.strictObject({
  /** Ontology `cycleTimeDelta` verbatim, e.g. "-3 days". Prose-capped, as `effortShift` is. */
  label: DescriptionSchema.nullable(),
  days: z.number().min(-3_650).max(3_650).nullable(),
});
export type CycleTimeDelta = z.infer<typeof CycleTimeDeltaSchema>;

/** Compare walks catalog bindings too, so its kind space is wider than the authorable one. */
export const DIFF_ELEMENT_KINDS = [...ELEMENT_KINDS, ...CATALOG_KINDS, 'deliverable'] as const;
export const DiffElementKindSchema = z.enum(DIFF_ELEMENT_KINDS);
export type DiffElementKind = z.infer<typeof DiffElementKindSchema>;

export const ElementDiffSchema = z.strictObject({
  elementId: ElementIdSchema,
  kind: DiffElementKindSchema,
  label: LabelSchema,
  change: ChangeKindSchema,
  /** Parent element id on whichever side exists, so the UI can group by stage. */
  parentId: ElementIdSchema.nullable(),
  propertyChanges: z.array(PropertyChangeSchema),
  roleMappings: z.array(RoleMappingSchema),
  automation: z.strictObject({
    left: AutomationLevelSchema.nullable(),
    right: AutomationLevelSchema.nullable(),
  }),
  effortShift: EffortShiftSchema.nullable(),
  gateModelChange: GateModelChangeSchema.nullable(),
  cycleTimeDelta: CycleTimeDeltaSchema.nullable(),
  /** Ontology `agenticShift` prose, when the right side carries it. */
  agenticShift: DescriptionSchema.nullable(),
});
export type ElementDiff = z.infer<typeof ElementDiffSchema>;

export const CompareSummarySchema = z.strictObject({
  addedCount: z.int().min(0),
  removedCount: z.int().min(0),
  changedCount: z.int().min(0),
  unchangedCount: z.int().min(0),
  /** Aggregate bands for the executive strip; null when neither side declares them. */
  totalEffortShift: EffortShiftSchema.nullable(),
  totalCycleTimeDelta: CycleTimeDeltaSchema.nullable(),
  gateModelChanges: z.int().min(0),
  newAgentCount: z.int().min(0),
  newToolCount: z.int().min(0),
  newAssetCount: z.int().min(0),
  newAgentSkillCount: z.int().min(0),
  newMcpServerCount: z.int().min(0),
});
export type CompareSummary = z.infer<typeof CompareSummarySchema>;

export const CompareMethodsRequestSchema = z.strictObject({
  leftId: IdentifierSchema,
  rightId: IdentifierSchema,
  /** Drop unchanged elements — the compare surface defaults to differences only. */
  includeUnchanged: z.stringbool().optional(),
});
export type CompareMethodsRequest = z.infer<typeof CompareMethodsRequestSchema>;
export const CompareMethodsResponseSchema = z.strictObject({
  left: MethodVersionSummarySchema,
  right: MethodVersionSummarySchema,
  summary: CompareSummarySchema,
  elements: z.array(ElementDiffSchema),
  /** Semantic diff of the two JSON-LD graphs, for the "show me the ontology" drawer. */
  semanticDifferences: z.array(
    z.strictObject({
      subject: z.string(),
      predicate: z.string().optional(),
      left: z.array(z.string()).optional(),
      right: z.array(z.string()).optional(),
    }),
  ),
  /** Diagnostics that only surface when comparing (dangling cross-variant references). */
  diagnostics: z.array(DiagnosticSchema),
  comparedAt: IsoDateTimeSchema,
});
export type CompareMethodsResponse = z.infer<typeof CompareMethodsResponseSchema>;
